// src/finance/payers.ts
// Ventilation des prélèvements de la simulation courante — par payeur
// (« Qui paie quoi ») et par segment du CA (barre de répartition).
// Aucun calcul fiscal ici : tout dérive du breakdown produit par le moteur.
import type { RegimeResult } from './engine/types'
import { round2 } from './engine/tax'

export type PayerAggregate = {
  company: { corporateTax: number; employerContrib: number; subtotal: number }
  personal: { employeeContrib: number; ir: number; pfu: number; subtotal: number }
  expenses: number
  totalLevies: number
  net: number
  leviesRate: number | null
}

export function aggregatePayers(r: RegimeResult): PayerAggregate {
  const b = r.breakdown
  const company = {
    corporateTax: b.dividends.ct,
    employerContrib: b.salary.employerContrib,
    subtotal: round2(b.dividends.ct + b.salary.employerContrib),
  }
  const personal = {
    employeeContrib: b.salary.employeeContrib,
    ir: b.salary.ir,
    pfu: b.dividends.pfu,
    subtotal: round2(b.salary.employeeContrib + b.salary.ir + b.dividends.pfu),
  }
  const totalLevies = round2(company.subtotal + personal.subtotal)
  return {
    company,
    personal,
    expenses: b.companyExpenses,
    totalLevies,
    net: r.netMonthly,
    leviesRate: r.revenueMonthly > 0 ? totalLevies / r.revenueMonthly : null,
  }
}

export type SplitKey = 'expenses' | 'company' | 'personal' | 'living' | 'net'
export type SplitSegment = { key: SplitKey; label: string; amount: number; share: number }

/**
 * Découpe le CA en segments, dans l'ordre du flux d'argent.
 * `personalExpensesMonthly` prolonge la chaîne jusqu'au reste après train de vie :
 * le segment `living` apparaît et `net` porte alors le RESTE (net − dépenses perso),
 * qui peut être négatif. Sans cet argument, le découpage s'arrête au net d'impôts.
 * `share` vaut 0 quand le CA est nul ; à perte la somme dépasse 1 (les dépenses
 * excèdent le CA) — c'est exact, et `flex-grow` normalise le rendu.
 */
export function revenueSplit(r: RegimeResult, personalExpensesMonthly = 0): SplitSegment[] {
  const a = aggregatePayers(r)
  const share = (amount: number) => (r.revenueMonthly > 0 ? amount / r.revenueMonthly : 0)
  const living = personalExpensesMonthly > 0
  const remaining = living ? round2(a.net - personalExpensesMonthly) : a.net
  return [
    { key: 'expenses', label: 'Dépenses', amount: a.expenses, share: share(a.expenses) },
    { key: 'company', label: 'Société', amount: a.company.subtotal, share: share(a.company.subtotal) },
    { key: 'personal', label: 'Perso', amount: a.personal.subtotal, share: share(a.personal.subtotal) },
    ...(living
      ? [
          {
            key: 'living' as const,
            label: 'Dépenses perso',
            amount: personalExpensesMonthly,
            share: share(personalExpensesMonthly),
          },
        ]
      : []),
    { key: 'net', label: living ? 'Reste' : 'Net', amount: remaining, share: share(remaining) },
  ]
}
